import React from 'react';
import {
  Cog6ToothIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChartBarIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
import PlotCustomization, { ColorByOption, MarkerByOption, SizeByOption } from './PlotCustomization';

interface AnalysisSettingsSidebarProps {
  plotType?: 'probability' | 'cdf';
  onPlotTypeChange?: (type: 'probability' | 'cdf') => void;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  onToggleConfidenceIntervals?: (enabled: boolean) => void;
  onConfidenceLevelChange?: (level: number) => void;
  showCensoredData?: boolean;
  onToggleCensoredData?: (enabled: boolean) => void;
  selectedMethod?: 'mle' | 'johnson';
  onMethodChange?: (method: 'mle' | 'johnson') => void;
  colorBy?: ColorByOption;
  onColorByChange?: (option: ColorByOption) => void;
  markerBy?: MarkerByOption;
  onMarkerByChange?: (option: MarkerByOption) => void;
  sizeBy?: SizeByOption;
  onSizeByChange?: (option: SizeByOption) => void;
  showLegend?: boolean;
  onShowLegendChange?: (show: boolean) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  showCollapseBothButton?: boolean;
  onCollapseBoth?: () => void;
}

const AnalysisSettingsSidebar: React.FC<AnalysisSettingsSidebarProps> = ({
  plotType = 'probability',
  onPlotTypeChange,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  onToggleConfidenceIntervals,
  onConfidenceLevelChange,
  showCensoredData = true,
  onToggleCensoredData,
  selectedMethod = 'mle',
  onMethodChange,
  colorBy = 'none',
  onColorByChange,
  markerBy = 'none',
  onMarkerByChange,
  sizeBy = 'uniform',
  onSizeByChange,
  showLegend = false,
  onShowLegendChange,
  isCollapsed = false,
  onToggleCollapse,
  showCollapseBothButton = false,
  onCollapseBoth,
}) => {

  if (isCollapsed) {
    return (
      <div className="bg-white border-r border-gray-200 h-full flex flex-col w-12">
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Expand analysis settings"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <Cog6ToothIcon className="h-6 w-6 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      <div className="lg:hidden fixed inset-0 z-50 flex">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onToggleCollapse} />
        <div className="relative flex flex-col w-full max-w-xs bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              onClick={onToggleCollapse}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Close settings</span>
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Analysis Settings</h3>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Mobile Content - Direct settings without collapsible sections */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Plot View</h4>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => onPlotTypeChange?.('probability')}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      plotType === 'probability'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <ChartBarIcon className="h-4 w-4" />
                    <span>Probability</span>
                  </button>
                  <button
                    onClick={() => onPlotTypeChange?.('cdf')}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      plotType === 'cdf'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <PresentationChartLineIcon className="h-4 w-4" />
                    <span>CDF</span>
                  </button>
                </div>
              </div>

              {/* Statistical Method - Only show if method selection is available */}
              {onMethodChange && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Statistical Method</h4>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <button
                      onClick={() => onMethodChange?.('mle')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        selectedMethod === 'mle'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      MLE
                    </button>
                    <button
                      onClick={() => onMethodChange?.('johnson')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        selectedMethod === 'johnson'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Johnson&apos;s
                    </button>
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Confidence Intervals</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Show Confidence Intervals
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showConfidenceIntervals}
                        onChange={(e) => onToggleConfidenceIntervals?.(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {showConfidenceIntervals && (
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <button
                        onClick={() => onConfidenceLevelChange?.(90)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          confidenceLevel === 90
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        90%
                      </button>
                      <button
                        onClick={() => onConfidenceLevelChange?.(95)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          confidenceLevel === 95
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        95%
                      </button>
                      <button
                        onClick={() => onConfidenceLevelChange?.(99)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          confidenceLevel === 99
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        99%
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Data Display</h4>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Show Censored Data
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCensoredData}
                      onChange={(e) => onToggleCensoredData?.(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Display censored observations on plots
                </p>
              </div>

              {/* Plot Customization */}
              <PlotCustomization
                colorBy={colorBy}
                onColorByChange={onColorByChange}
                markerBy={markerBy}
                onMarkerByChange={onMarkerByChange}
                sizeBy={sizeBy}
                onSizeByChange={onSizeByChange}
                showLegend={showLegend}
                onShowLegendChange={onShowLegendChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex bg-white border-r border-gray-200 h-full flex-col w-80">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Analysis Settings</h3>
            </div>
            <div className="flex items-center space-x-1">
              {showCollapseBothButton && onCollapseBoth && (
                <button
                  onClick={onCollapseBoth}
                  className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Collapse all sidebars"
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={onToggleCollapse}
                className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                title="Close analysis settings"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Configure analysis parameters
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Plot Type Toggle */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <PresentationChartLineIcon className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-700">Plot View</h4>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <button
                onClick={() => onPlotTypeChange?.('probability')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  plotType === 'probability'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChartBarIcon className="h-4 w-4" />
                <span>Probability</span>
              </button>
              <button
                onClick={() => onPlotTypeChange?.('cdf')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  plotType === 'cdf'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <PresentationChartLineIcon className="h-4 w-4" />
                <span>CDF</span>
              </button>
            </div>
          </div>

          {/* Statistical Method - Only show if method selection is available */}
          {onMethodChange && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <ChartBarIcon className="h-4 w-4 text-blue-600" />
                <h4 className="text-sm font-medium text-gray-700">Statistical Method</h4>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <button
                  onClick={() => onMethodChange?.('mle')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedMethod === 'mle'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  MLE
                </button>
                <button
                  onClick={() => onMethodChange?.('johnson')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedMethod === 'johnson'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Johnson&apos;s
                </button>
              </div>
            </div>
          )}

          {/* Confidence Intervals */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Cog6ToothIcon className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-700">Confidence Intervals</h4>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Show Confidence Intervals
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showConfidenceIntervals}
                    onChange={(e) => onToggleConfidenceIntervals?.(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              {showConfidenceIntervals && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => onConfidenceLevelChange?.(90)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      confidenceLevel === 90
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    90%
                  </button>
                  <button
                    onClick={() => onConfidenceLevelChange?.(95)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      confidenceLevel === 95
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    95%
                  </button>
                  <button
                    onClick={() => onConfidenceLevelChange?.(99)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      confidenceLevel === 99
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    99%
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Censored Data Toggle */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <ChartBarIcon className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-700">Data Display</h4>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Show Censored Data
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCensoredData}
                  onChange={(e) => onToggleCensoredData?.(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Display censored observations on plots
            </p>
          </div>

          {/* Plot Customization */}
          <PlotCustomization
            colorBy={colorBy}
            onColorByChange={onColorByChange}
            markerBy={markerBy}
            onMarkerByChange={onMarkerByChange}
            sizeBy={sizeBy}
            onSizeByChange={onSizeByChange}
            showLegend={showLegend}
            onShowLegendChange={onShowLegendChange}
          />
        </div>
      </div>
    </>
  );
};

export default AnalysisSettingsSidebar;